<script lang="ts" module>
	import { type Snippet, tick } from "svelte";
	import type { ActionReturn } from "svelte/action";

	/**
	 * Usage: <div use:portal={'css selector'}> or <div use:portal={document.body}>
	 *
	 * @param el
	 * @param target DOM Element or CSS Selector
	 */
	export function portal(
		el: HTMLElement,
		target: HTMLElement | string | null = "body",
	): ActionReturn<HTMLElement | string> {
		let targetEl: HTMLElement | null = null;

		function update(newTarget: HTMLElement | null | string): void {
			void (async function (): Promise<void> {
				target = newTarget;
				if (typeof target === "string") {
					targetEl = document.querySelector(target);
					if (targetEl === null) {
						await tick();
						// eslint-disable-next-line require-atomic-updates
						targetEl = document.querySelector(target);
					}
					if (targetEl === null) {
						throw new Error(`No element found matching css selector: "${target}"`);
					}
				} else if (target instanceof HTMLElement) {
					targetEl = target;
				} else {
					throw new TypeError(`Target must be a DOM Element or a CSS Selector string. Received: ${typeof target}`);
				}
				targetEl.appendChild(el);
				el.hidden = false;
			})();
		}

		function destroy(): void {
			if (el.parentNode) {
				el.parentNode.removeChild(el);
			}
		}

		update(target);

		return {
			update,
			destroy,
		};
	}
</script>

<script lang="ts">
	const { target = "body", children }: { target?: HTMLElement | string; children: Snippet } = $props();
</script>

<div use:portal={target} hidden>
	{@render children()}
</div>
